import React, { useEffect, useState } from "react"
import { Container, Row, Col, Card, CardBody, Table, Button } from "reactstrap"
import _ from "lodash"

import * as backend from "../../helpers/backend_helper"
import Toaster from "../../components/Common/Toaster"

// A clickable selectable chip (matches the Validation config tab).
const Chip = ({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      border: `1px solid ${on ? "#2ea99b" : "#cdd5dd"}`,
      borderRadius: 14,
      padding: "3px 12px",
      margin: 3,
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      background: on ? "#2ea99b" : "#fff",
      color: on ? "#fff" : "#6b7785",
    }}
  >
    {on ? "✓ " : ""}{label}
  </button>
)

// A Yes / No pill toggle.
const YesNo = ({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    style={{
      border: `1px solid ${value ? "#2ea99b" : "#cdd5dd"}`,
      borderRadius: 8,
      padding: "6px 22px",
      fontSize: 13,
      fontWeight: 700,
      cursor: "pointer",
      background: value ? "#2ea99b" : "#fff",
      color: value ? "#fff" : "#6b7785",
      minWidth: 72,
    }}
  >
    {value ? "Yes" : "No"}
  </button>
)

// Assign Tasks config (admins): for each active user decide whether they may
// assign tasks, and to which users they may assign (and validate) them.
const AssignTasks = (props: any) => {
  const { factory } = props
  const orgId = factory && factory.orgId
  const facId = factory && factory._id

  const [userList, setUserList] = useState<any[]>([])
  // config[userId] = { canAssign: boolean, users: string[] }
  const [config, setConfig] = useState<{ [uid: string]: { canAssign: boolean; users: string[] } }>({})

  useEffect(() => {
    backend.getUserOrg(orgId).then(setUserList).catch(() => {})
  }, [orgId])

  useEffect(() => {
    const initial: any = {}
    ;((factory && factory.taskConfig) || []).forEach((e: any) => {
      initial[String(e.userId)] = { canAssign: !!e.canAssign, users: (e.users || []).map(String) }
    })
    setConfig(initial)
    // Seed only when the factory changes so edits aren't wiped on re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factory && factory._id])

  const activeTeams = (factory && factory.teams ? factory.teams : []).filter((t: any) => t.status !== false)

  // Every active team member (name + role), resolved against the org user list.
  const people = activeTeams
    .map((t: any) => {
      const u = _.find(userList, ["_id", t.userId])
      return u ? { userId: String(t.userId), name: u.name, role: u.role } : null
    })
    .filter(Boolean) as any[]

  const userOptions = people.map((p) => ({ value: p.userId, label: `${p.name} (${p.role})` }))

  const setCanAssign = (uid: string, val: boolean) => {
    setConfig((c) => {
      const cur = c[uid] || { canAssign: false, users: [] }
      return { ...c, [uid]: { ...cur, canAssign: val } }
    })
  }

  const toggleUser = (uid: string, val: string) => {
    setConfig((c) => {
      const cur = c[uid] || { canAssign: false, users: [] }
      const list = cur.users || []
      const next = list.includes(val) ? list.filter((v) => v !== val) : [...list, val]
      return { ...c, [uid]: { ...cur, users: next } }
    })
  }

  const submit = async () => {
    const taskConfig = people
      .map((p) => ({
        userId: p.userId,
        canAssign: !!(config[p.userId] && config[p.userId].canAssign),
        // A user can never be in their own assignable list.
        users: ((config[p.userId] && config[p.userId].users) || []).filter((uid: string) => uid !== p.userId),
      }))
      .filter((e) => e.canAssign && e.users.length > 0)
    try {
      await backend.updateFactory(orgId, facId, { taskConfig })
      props.update && props.update()
      Toaster.success("Assign Tasks configuration saved")
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Failed to save Assign Tasks configuration")
    }
  }

  return (
    <Container fluid>
      <div className="text-muted" style={{ fontSize: 13, lineHeight: 1.7 }}>
        <p className="mb-0">
          Choose which active users are <strong>allowed to assign tasks</strong>, and for each of them the
          <strong> users they can assign tasks to and validate</strong>. Everyone can see tasks assigned to them;
          only the users set here can create tasks for others.
        </p>
      </div>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              {people.length === 0 ? (
                <div className="mw-empty-state">
                  No active team members yet. Add users to this factory (Teams tab) and they&apos;ll show up here.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table mw-table table-bordered mb-0">
                    <thead>
                      <tr className="table-success">
                        <th style={{ minWidth: 180 }}>Individual</th>
                        <th style={{ minWidth: 140, textAlign: "center" }}>Allow to assign tasks</th>
                        <th style={{ minWidth: 320 }}>Users they can assign task and validate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {people.map((p) => {
                        const cur = config[p.userId] || { canAssign: false, users: [] }
                        return (
                          <tr key={p.userId}>
                            <th scope="row">
                              {p.name} <small className="text-muted">({p.role})</small>
                            </th>
                            <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                              <YesNo value={cur.canAssign} onChange={(v) => setCanAssign(p.userId, v)} />
                            </td>
                            <td>
                              {!cur.canAssign ? (
                                <span className="text-muted">—</span>
                              ) : (
                                <div style={{ display: "flex", flexWrap: "wrap" }}>
                                  {/* A user cannot assign tasks to themselves. */}
                                  {userOptions.filter((o) => o.value !== p.userId).map((o) => (
                                    <Chip
                                      key={o.value}
                                      label={o.label}
                                      on={(cur.users || []).includes(o.value)}
                                      onClick={() => toggleUser(p.userId, o.value)}
                                    />
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
              {people.length > 0 && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
                  <Button color="success" onClick={submit}>Save</Button>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default AssignTasks
